import { DateTime } from '@mode-switch/express';
import { ChartFacade } from '@mode/charts/contract';
import {
  FLAMINGO_RESERVED_REFS,
  formatTimeAgo,
  getFlamingoRawDataFromDataset,
  isPresent,
  makePagingResult,
  prepareDataForCopying,
  shareReplayDefault,
} from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { escapeRegExp } from 'lodash';
import { combineLatest as staticCombineLatest } from 'rxjs';
import { combineLatest, filter, map, startWith, switchMap } from 'rxjs/operators';
import { reportContextSelector } from '~/reducers/selectors';
import { ChartDesignerSelectors } from '~/selectors/designer.selectors';
import { removeNonActiveFilters } from '../lib/chart-encoding-helpers';
import { ActiveExplorationSelectors } from '../selectors/active-exploration.selectors';
import { AppStore } from '../types/main.types';
import { ExplorationObservables } from '../types/viz-designer.types';

/**
 * Make the base observables object for explorations.
 *
 * Should not use legacy SelectedReportState
 *
 * TODO: There are some dependencies of features used here still using data from
 * SelectedReportState. This will be refactored in the future to use DataView state,
 * but won't completely happen until report flow efforts are further along.
 */
export function makeExplorationObservables(
  store: AppStore,
  dataViewFacade: DataViewFacade,
  chartFacade: ChartFacade,
  flamingoFacade: FlamingoFacade
): ExplorationObservables {
  const vizToken$ = store.selectNoEmpty(ActiveExplorationSelectors.getVizToken);

  /**
   * Viz based items.
   */

  const name$ = store.select(ActiveExplorationSelectors.getTitle);

  const switchViewToken$ = store.selectNoEmpty(ActiveExplorationSelectors.getSwitchViewToken);

  const switchViewName$ = switchViewToken$.pipe(
    switchMap((token) => dataViewFacade.getSwitchViewNameBySwitchViewToken(token))
  );

  const switchViewLastSuccessfulRunAt$ = switchViewToken$.pipe(
    switchMap((token) => dataViewFacade.getDatasetResultCompletedAtBySwitchViewToken(token))
  );

  const title$ = chartFacade.createChartTitleFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const description$ = chartFacade.createChartDescriptionFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const chartType$ = chartFacade
    .createChartTypeFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const queryToken$ = chartFacade
    .createQueryTokenFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const queryRunToken$ = queryToken$.pipe(
    switchMap((queryToken) => dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken))
  );

  const encoding$ = chartFacade.createEncodingFromVizToken(FLAMINGO_RESERVED_REFS.Exploration).pipe(filter(isPresent));

  const format$ = chartFacade.createChartFormatFromVizToken(FLAMINGO_RESERVED_REFS.Exploration).pipe(filter(isPresent));

  const fieldFormats$ = chartFacade.createChartFieldFormatMapFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const fieldColors$ = chartFacade.createChartFieldColorMapFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const colorPaletteToken$ = chartFacade
    .createPaletteFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const facetSizes$ = chartFacade.createChartFacetSizeMapFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const isAutoUpdateDisabled$ = chartFacade.createIsAutoUpdateDisabledFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);
  const manuallyApplyChanges$ = chartFacade.createManuallyApplyChangesFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);
  const isAutoOrManualRender$ = staticCombineLatest([isAutoUpdateDisabled$, manuallyApplyChanges$]).pipe(
    map(([isAutoUpdateDisabled, manuallyApplyChanges]) => !isAutoUpdateDisabled || manuallyApplyChanges)
  );
  // The unappliedChangesCount may be negative if the changes are a result of the user undoing
  // previously applied changes. This observable maps to the absolute value so that the consumers
  // just see the raw number of changes.
  const unappliedChangesCount$ = chartFacade
    .createUnappliedChangesFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(map((unappliedChanges) => Math.abs(unappliedChanges)));

  const caption$ = chartFacade.createCaptionFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const explorationOwnerUserName$ = store.select(ActiveExplorationSelectors.getExplorationOwnerUserName);
  const explorationOwnerName$ = store.select(ActiveExplorationSelectors.getExplorationOwnerName);
  const explorationOwnerAvatar$ = store.select(ActiveExplorationSelectors.getExplorationOwnerAvatar);
  const reportDisplayName$ = store.select(ActiveExplorationSelectors.getGfReportDisplayName);

  /**
   * Run related items.
   */

  const gfRunState$ = store.selectNoEmpty(ActiveExplorationSelectors.getGfRunState);

  const runFailed$ = gfRunState$.pipe(
    map((state) => state === 'failed'),
    shareReplayDefault()
  );

  /**
   * Plan related items.
   */

  const designerPlanId$ = flamingoFacade.createPlanIdFromRefId(FLAMINGO_RESERVED_REFS.Exploration);

  const binSizePlanId$ = flamingoFacade.createPlanIdFromRefId(
    `${FLAMINGO_RESERVED_REFS.BinSize}#${FLAMINGO_RESERVED_REFS.Exploration}`
  );

  const planFailed$ = designerPlanId$.pipe(switchMap((planId) => flamingoFacade.createFailedByPlanId(planId)));

  const planSucceeded$ = designerPlanId$.pipe(switchMap((planId) => flamingoFacade.createReadyFromPlanId(planId)));

  const planId$ = designerPlanId$.pipe(
    map((designerPlanId) => ({ token: FLAMINGO_RESERVED_REFS.Exploration, planId: designerPlanId })),
    shareReplayDefault()
  );

  const planError$ = planId$.pipe(
    switchMap(({ planId }) => flamingoFacade.createErrorFromPlanId(planId)),
    shareReplayDefault()
  );

  // TODO - This will need to be migrated to use the new paging feature before this file
  //   can be moved to an observable creator in a component facade as we'll need to remove
  //   the legacy `ChartDesignerSelectors` usage.
  const paging$ = planId$.pipe(
    switchMap(({ planId, token }) =>
      staticCombineLatest([
        store.select(ChartDesignerSelectors.createPagingSelector(token)),
        flamingoFacade.createCountFromPlanId(planId).pipe(filter(isPresent)),
      ]).pipe(map(([paging, count]) => makePagingResult(paging, count)))
    ),
    filter((p) => p != null)
  );

  /**
   * View/model.
   */

  const model$ = dataViewFacade.model$;

  const view$ = staticCombineLatest([queryToken$, model$]).pipe(
    map(([queryToken, model]) => Switch.Views.find(model, queryToken)),
    shareReplayDefault()
  );

  /**
   * Loading and state related items.
   */
  const isValid$ = chartFacade.createIsValidFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const isComplete$ = chartFacade.createIsCompleteFromVizToken(FLAMINGO_RESERVED_REFS.Exploration);

  const canRender$ = staticCombineLatest([isValid$, isComplete$]).pipe(
    map(([isValid, isComplete]) => isValid && isComplete)
  );

  const planLoading$ = designerPlanId$.pipe(
    switchMap((planId) => flamingoFacade.createLoadingByPlanId(planId)),
    combineLatest(canRender$),
    map(([loading, canRender]) => loading && canRender)
  );

  const binSizePlanLoading$ = binSizePlanId$.pipe(switchMap((planId) => flamingoFacade.createLoadingByPlanId(planId)));

  const planPageLoading$ = designerPlanId$.pipe(
    switchMap((planId) => flamingoFacade.createPageLoadingByPlanId(planId)),
    combineLatest(canRender$),
    map(([loading, canRender]) => loading && canRender)
  );

  const explorationQueryExists$ = store.select(ActiveExplorationSelectors.gfQueryExists);

  // TODO: replace with more explicit method after Miki's next update to the api
  // This observable is piped off of `explorationQueryExists$` to demonstrate that it's only valid to use this
  // if we can assume that the query already exists. If the query doesn't exist, this will never resolve.
  const isViewLoaded$ = explorationQueryExists$.pipe(
    combineLatest(view$, (queryExists, view) => {
      if (queryExists && view !== undefined) {
        return true;
      }
    }),
    filter((isLoaded) => isLoaded !== undefined),
    startWith(false)
  );

  /**
   * Composed items - the rest are either results or composed of data from multiple sources.
   */

  const chartState$ = store.select(ActiveExplorationSelectors.getGfExplorationChartState);

  const renderModel$ = planId$.pipe(
    switchMap(({ token, planId }) => chartFacade.createRenderDataModel(FLAMINGO_RESERVED_REFS.Exploration, planId))
  );

  const columns$ = flamingoFacade.createColumnsByRefId(FLAMINGO_RESERVED_REFS.Exploration);

  const chartData$ = planId$.pipe(
    switchMap(({ planId, token }) =>
      chartFacade.createFlamingoChartSummary(token, planId).pipe(
        filter((result) => result !== undefined) // nulls should pass through
        // first() // @TODO: Temporarily removed `first()` as this observable from the facade doesn't behave
        // exactly the same as the selector taken directly.
      )
    )
  );

  const datasetForCopy$ = chartData$.pipe(map((data) => prepareDataForCopying(getFlamingoRawDataFromDataset(data))));

  const explorationQueryName$ = store.select(ActiveExplorationSelectors.getGfQueryName);

  const explorationUpdatedAt$ = store
    .select(ActiveExplorationSelectors.getExplorationUpdatedAt)
    .pipe(map((updatedAtDate) => formatTimeAgo(updatedAtDate)));

  const queryCompletedAt$ = queryToken$
    .pipe(switchMap((queryToken) => store.selectNoEmpty(ActiveExplorationSelectors.getGfQueryRunCompletedAt)))
    .pipe(map((completedAtDate) => formatTimeAgo(completedAtDate)));

  const runRawSql$ = store.selectNoEmpty(ActiveExplorationSelectors.getGfRunRawSql);

  // TODO: This is currently working off of state, but the params that come back from the api don't have all properties
  // like the correct `values`, so the actual output is incorrect, will touch base with Miki
  const usedParameters$ = runRawSql$.pipe(
    switchMap(
      () => store.select(ActiveExplorationSelectors.getGfReportRunFormFields),
      (runRawSql, reportRunFormFields) => {
        const usedParameters = reportRunFormFields.filter((item) => {
          var paramRegex = new RegExp(`{{\\s*${escapeRegExp(item.name)}\\s*}}`);
          return paramRegex.test(runRawSql);
        });

        return usedParameters;
      }
    )
  );

  const activeFilters$ = encoding$.pipe(
    map(({ filter }) => {
      return removeNonActiveFilters(filter);
    })
  );

  const dataProfiling$ = flamingoFacade
    .createDataProfilingFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const binSizeToFieldMap$ = flamingoFacade
    .createBinSizeToFieldMapFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const conditionalFormatting$ = flamingoFacade
    .createConditionalFormattingFromVizToken(FLAMINGO_RESERVED_REFS.Exploration)
    .pipe(filter(isPresent));

  const defaultYearStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.yearStart(reportContext.settings?.['year_start']) ?? DateTime.YearStart.January)
    );

  const defaultWeekStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.weekStart(reportContext.settings?.['week_start']) ?? DateTime.WeekStart.Sunday)
    );

  return {
    name$,
    title$,
    description$,
    chartType$,
    token$: vizToken$,
    queryToken$,
    queryRunToken$,
    switchViewToken$,
    switchViewName$,
    switchViewLastSuccessfulRunAt$,
    encoding$,
    format$,
    fieldFormats$,
    fieldColors$,
    facetSizes$,
    isAutoUpdateDisabled$,
    manuallyApplyChanges$,
    isAutoOrManualRender$,
    unappliedChangesCount$: unappliedChangesCount$,
    caption$,
    activeFilters$,
    colorPaletteToken$,
    explorationOwnerUserName$,
    explorationOwnerName$,
    explorationOwnerAvatar$,
    explorationUpdatedAt$,
    reportDisplayName$,

    isValid$,
    isComplete$,
    canRender$,
    runFailed$,
    planError$,
    planLoading$,
    planPageLoading$,
    planFailed$,
    planSucceeded$,
    queryCompletedAt$,
    usedParameters$,

    planId$,
    model$,
    view$,
    chartState$,
    renderModel$,
    columns$,
    paging$,
    chartData$,
    datasetForCopy$,

    explorationQueryExists$,
    explorationQueryName$,
    isViewLoaded$,
    dataProfiling$,
    binSizeToFieldMap$,
    conditionalFormatting$,
    binSizePlanLoading$,

    defaultYearStart$,
    defaultWeekStart$,
  };
}
